import { Button } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getItemWithExpiry } from '@wavvest/shared-utils';

import { AdvisorSignupLayout } from '@/pages/login/AdvisorSignupLayout';
import config from '@/services/config';
import { useAppDispatch } from '@/store/hooks';
import { fetchFeatureFlags } from '@/store/features/userinfo/userinfoSlice';
import {
  createBillingRecoverySession,
  getBillingRecoveryState,
  getEntitlementStatus,
  refreshBillingEntitlement,
} from '@/features/subscription-entitlement/api';
import type { BillingRecoveryState } from '@/features/subscription-entitlement/api';
import { consumeSubscriptionReturnPath } from '@/features/subscription-entitlement/navigation';

type PageState = 'loading' | 'blocked' | 'unavailable';

const recoveryCopy: Record<BillingRecoveryState['action'], string> = {
  setup_billing: 'Choose a plan',
  manage_billing: 'Manage billing',
  update_payment_method: 'Update payment method',
  resubscribe: 'Choose a new plan',
  resume_or_contact_support: 'Resume subscription',
  contact_support: 'Contact support',
};

const getBillingError = (error: unknown): { kind?: string; reason?: string } => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'object' && detail !== null ? detail : {};
};

const SubscriptionRequiredPage = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authenticated = Boolean(getItemWithExpiry('access_token'));
  const [supportRequired, setSupportRequired] = useState(false);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [recovery, setRecovery] = useState<BillingRecoveryState | null>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [startingRecovery, setStartingRecovery] = useState(false);
  const [retrySequence, setRetrySequence] = useState(0);

  const restoreIntendedRoute = useCallback(() => {
    navigate(consumeSubscriptionReturnPath(), { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!authenticated) return;

    let active = true;
    const load = async () => {
      setPageState('loading');
      setSupportRequired(false);
      setRecovery(null);
      setClientSecret('');
      try {
        const params = new URLSearchParams(location.search);
        const isBillingReturn = params.get('billing_return') === '1';
        const managingBilling = params.get('manage') === '1';
        let profileRefreshed = false;
        const refresh = async () => {
          const result = await refreshBillingEntitlement();
          try {
            await dispatch(fetchFeatureFlags(true)).unwrap();
            profileRefreshed = true;
          } catch {
            // Billing recovery can proceed, but cached workspace access is not ready.
            profileRefreshed = false;
          }
          return result;
        };
        if (isBillingReturn) {
          const refreshed = await refresh();
          if (!active) return;
          if (refreshed.entitled && !managingBilling && profileRefreshed) {
            restoreIntendedRoute();
            return;
          }
        }

        if (!managingBilling && !isBillingReturn) {
          try {
            const entitlement = await getEntitlementStatus();
            if (!active) return;
            if (entitlement.entitled) {
              restoreIntendedRoute();
              return;
            }
            if (!entitlement.can_manage_billing) {
              setCanManageBilling(false);
              setPageState('blocked');
              return;
            }
          } catch (error) {
            // An uninitialized snapshot must not prevent reaching billing recovery.
            if (getBillingError(error).reason !== 'missing_snapshot') throw error;
          }
        }
        let state = await getBillingRecoveryState();
        if (!active) return;
        if (state.reason === 'missing_status') {
          await refresh();
          state = await getBillingRecoveryState();
          if (!active) return;
        }
        setCanManageBilling(state.can_manage_billing);
        setRecovery(state);
        setPageState('blocked');
      } catch (error) {
        if (!active) return;
        const detail = getBillingError(error);
        if (detail.kind === 'BILLING_ADMIN_REQUIRED') {
          setCanManageBilling(false);
          setPageState('blocked');
        } else {
          setSupportRequired(detail.kind === 'BILLING_RECOVERY_UNAVAILABLE');
          setPageState('unavailable');
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [authenticated, dispatch, location.search, restoreIntendedRoute, retrySequence]);

  const startRecovery = async () => {
    if (!recovery?.self_service_available) return;
    if (recovery.action === 'setup_billing') {
      navigate('/stripe-config');
      return;
    }
    setStartingRecovery(true);
    try {
      const session = await createBillingRecoverySession();
      if (session.session_type === 'billing_setup') {
        navigate('/stripe-config');
        return;
      }
      if (session.session_type === 'billing_portal' && session.url) {
        window.location.assign(session.url);
        return;
      }
      if (session.session_type === 'customer_session' && session.client_secret) {
        setClientSecret(session.client_secret);
      } else {
        setPageState('unavailable');
      }
    } catch (error) {
      setSupportRequired(getBillingError(error).kind === 'BILLING_RECOVERY_UNAVAILABLE');
      setPageState('unavailable');
    } finally {
      setStartingRecovery(false);
    }
  };

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AdvisorSignupLayout showProgress={false}>
      <div className="advisor-signup-form-block advisor-stripe-content">
        <div className="advisor-signup-copy">
          <h1>Workspace billing</h1>
          {pageState === 'loading' && <p>Checking your workspace subscription...</p>}
          {pageState === 'unavailable' && (
            <>
              <p>
                {supportRequired
                  ? 'Billing needs a review. Please contact Wavvest support.'
                  : 'We could not verify or update your billing right now. Please try again.'}
              </p>
              <Button type="primary" onClick={() => setRetrySequence((value) => value + 1)}>
                Try again
              </Button>
            </>
          )}
          {pageState === 'blocked' && !canManageBilling && (
            <>
              <p>
                Ask a firm administrator to update the workspace subscription. Check again after
                they restore access.
              </p>
              <Button onClick={() => setRetrySequence((value) => value + 1)}>Check again</Button>
            </>
          )}
          {pageState === 'blocked' && canManageBilling && recovery && (
            <>
              <p>
                {recovery.reason === 'no_subscription'
                  ? 'Your workspace has no subscription. Choose a plan to get started.'
                  : recovery.self_service_available
                    ? 'Manage your workspace subscription and payment details.'
                    : 'Self-service recovery is not available for this workspace. Please contact Wavvest support.'}
              </p>
              {recovery.self_service_available && !clientSecret && (
                <Button type="primary" loading={startingRecovery} onClick={startRecovery}>
                  {recoveryCopy[recovery.action]}
                </Button>
              )}
              {!recovery.self_service_available && (
                <Button onClick={() => setRetrySequence((value) => value + 1)}>Check again</Button>
              )}
            </>
          )}
        </div>
        {clientSecret && (
          <div className="stripe-container">
            <stripe-pricing-table
              pricing-table-id={config.stripePricingTableId}
              publishable-key={config.stripePublishableKey}
              customer-session-client-secret={clientSecret}
            ></stripe-pricing-table>
          </div>
        )}
      </div>
    </AdvisorSignupLayout>
  );
};

export default SubscriptionRequiredPage;
